import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { InputProps, OutlinedInputProps, TextField, TextFieldProps } from "@mui/material";
import clsx from "clsx";
import { ChangeEvent, ComponentPropsWithoutRef, useState } from "react";
import { Controller, FieldValues, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import styles from "./FormInput.module.scss";

type IFormInputProps = {
    openTextCounter?: number;
    name?: string;
    children?: React.ReactNode;
    setValue?: (value: unknown) => void;
    type?: "text" | "number" | "password" | "email";
    otherInputProps?: TextFieldProps;
    readOnly?: boolean;
    InputProps?: Partial<OutlinedInputProps | InputProps>;
} & ComponentPropsWithoutRef<typeof TextField>;

export const MInput = (inputProps: IFormInputProps, className: string) => {
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const {
        type = "text",
        required = true,
        onChange,
        value,
        variant,
        label,
        openTextCounter,
        readOnly = false,
        InputProps: customInputProps,
        ...otherInputProps
    } = inputProps;
    return (
        <TextField
            onChange={onChange}
            value={value}
            fullWidth
            required={required}
            type={type === "password" ? (showPassword ? "text" : "password") : type}
            variant={variant}
            size='small'
            label={label}
            className={clsx(styles.customInput)}
            InputProps={{
                readOnly: readOnly,
                ...customInputProps,
                ...(type === "password" && {
                    endAdornment: (
                        <div
                            className={styles.controlShowPassword}
                            onClick={() => {
                                setShowPassword(!showPassword);
                            }}
                        >
                            {showPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                        </div>
                    ),
                }),
                ...(openTextCounter !== undefined && {
                    endAdornment: (
                        <div className={clsx(styles.textCounter, { [styles.error]: otherInputProps.error })}>
                            {value?.toString().length || 0} / {openTextCounter}
                        </div>
                    ),
                }),
                sx: { className },
            }}
            {...(variant === "filled" && {
                InputProps: { disableUnderline: true },
            })}
            {...otherInputProps}
        />
    );
};
const FormInput = (inputProps: IFormInputProps) => {
    const { name, error, className, onChange, ...otherInputProps } = inputProps;
    const { t } = useTranslation();

    const { control } = useFormContext<FieldValues>();

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange && onChange(e);
    };
    return (
        <Controller
            name={name || "unknown"}
            control={control}
            defaultValue={""}
            render={({ field: { onChange, value, onBlur }, fieldState: { error }, formState }) => {
                return (
                    <MInput
                        className={className}
                        name={name}
                        value={value}
                        onChange={(e) => {
                            onChange(e);
                            handleChange(e);
                        }}
                        onBlur={onBlur}
                        helperText={error ? t(error?.message as string) : null}
                        error={!!error}
                        {...otherInputProps}
                    />
                );
            }}
        />
    );
};
export default FormInput;
